import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios';
import { useBreadcrumb } from '../App';
import dayjs from 'dayjs';

interface InsuranceRepresentative {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  title: string | null;
  status: string;
  insurerId: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  insurer: {
    id: string;
    name: string;
    code: string | null;
  };
}

interface Insurer {
  id: string;
  name: string;
  code: string | null;
}

interface Claim {
  id: string;
  claimNumber: string;
  file_number: string;
  dateReported: string;
  status: string;
  description?: string;
  amount?: number;
  createdAt: string;
  insuredName?: string;
}

const InsuranceRepresentativeDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<InsuranceRepresentative>>({});
  const { setLastItem } = useBreadcrumb();

  // Fetch representative details
  const { data: repData, isLoading, error } = useQuery({
    queryKey: ['insuranceRepresentative', id],
    queryFn: async (): Promise<{ success: boolean; data: InsuranceRepresentative }> => {
      const response = await api.get<{ success: boolean; data: InsuranceRepresentative }>(`/insurance-reps/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch insurers for dropdown
  const { data: insurersData } = useQuery({
    queryKey: ['insurers-dropdown'],
    queryFn: async (): Promise<{ success: boolean; data: Insurer[] }> => {
      const response = await api.get<{ success: boolean; data: Insurer[] }>('/insurers/dropdown');
      return response.data;
    },
  });

  // Fetch related claims
  const { data: claimsData, isLoading: claimsLoading } = useQuery({
    queryKey: ['representativeClaims', id],
    queryFn: async (): Promise<{ success: boolean; data: Claim[] }> => {
      const response = await api.get<{ success: boolean; data: Claim[] }>('/claims', {
        params: {
          representativeId: id,
          limit: 10,
          sort: 'createdAt:desc'
        }
      });
      return response.data;
    },
    enabled: !!id,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: Partial<InsuranceRepresentative>) => {
      const response = await api.put<{ success: boolean; data: InsuranceRepresentative }>(`/insurance-reps/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['insuranceRepresentative', id] });
      queryClient.invalidateQueries({ queryKey: ['insuranceRepresentatives'] });
      setIsEditing(false);
    },
  });

  useEffect(() => {
    if (repData?.success && repData.data) {
      setEditForm({
        firstName: repData.data.firstName,
        lastName: repData.data.lastName,
        email: repData.data.email,
        phone: repData.data.phone,
        title: repData.data.title,
        status: repData.data.status,
        insurerId: repData.data.insurerId,
      });
    }
  }, [repData]);

  // Set breadcrumb with representative's name
  useEffect(() => {
    if (repData?.success && repData.data) {
      const repName = `${repData.data.firstName} ${repData.data.lastName}`.trim();
      setLastItem(repName);
    }
    return () => setLastItem(undefined);
  }, [repData, setLastItem]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    if (repData?.success && repData.data) {
      setEditForm({
        firstName: repData.data.firstName,
        lastName: repData.data.lastName,
        email: repData.data.email,
        phone: repData.data.phone,
        title: repData.data.title,
        status: repData.data.status,
        insurerId: repData.data.insurerId,
      });
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editForm);
  };

  const handleInputChange = (field: keyof InsuranceRepresentative, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !repData?.success) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load insurance representative details. Please try again.
        </Alert>
      </Box>
    );
  }

  const representative = repData.data;
  const insurers = insurersData?.data || [];
  const claims = claimsData?.data || [];

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/insurance-representatives')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          {representative.firstName} {representative.lastName}
        </Typography>
        <Box ml="auto">
          {!isEditing ? (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
          ) : (
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {updateMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to update representative. Please try again.
        </Alert>
      )}

      {/* Success Alert */}
      {updateMutation.isSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Representative updated successfully!
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Personal Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <PersonIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Personal Information</Typography>
              </Box>
              
              {isEditing ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="First Name"
                    value={editForm.firstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Last Name"
                    value={editForm.lastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Email"
                    type="email"
                    value={editForm.email || ''}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Phone"
                    value={editForm.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Title"
                    value={editForm.title || ''}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    fullWidth
                  />
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Name</Typography>
                    <Typography variant="body1">
                      {representative.firstName} {representative.lastName}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="textSecondary">Email</Typography>
                    {representative.email ? (
                      <Chip
                        icon={<EmailIcon />}
                        label={representative.email}
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="No email provided" color="warning" variant="outlined" />
                    )}
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="textSecondary">Phone</Typography>
                    {representative.phone ? (
                      <Chip
                        icon={<PhoneIcon />}
                        label={representative.phone}
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="No phone provided" color="warning" variant="outlined" />
                    )}
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="textSecondary">Title</Typography>
                    <Typography variant="body1">
                      {representative.title || 'No title specified'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Insurance Company Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <BusinessIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Insurance Company Information</Typography>
              </Box>
              
              {isEditing ? (
                <Box display="flex" flexDirection="column" gap={2}>
                  <FormControl fullWidth required>
                    <InputLabel>Insurance Company</InputLabel>
                    <Select
                      value={editForm.insurerId || ''}
                      label="Insurance Company"
                      onChange={(e) => handleInputChange('insurerId', e.target.value)}
                    >
                      {insurers
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((insurer) => (
                          <MenuItem key={insurer.id} value={insurer.id}>
                            {insurer.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={editForm.status || 'ACTIVE'}
                      label="Status"
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <MenuItem value="ACTIVE">Active</MenuItem>
                      <MenuItem value="INACTIVE">Inactive</MenuItem>
                      <MenuItem value="PENDING">Pending</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">Insurance Company</Typography>
                    <Chip
                      icon={<BusinessIcon />}
                      label={representative.insurer.name}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="textSecondary">Status</Typography>
                    <Chip
                      label={representative.status}
                      color={
                        representative.status === 'ACTIVE' ? 'success' :
                        representative.status === 'INACTIVE' ? 'error' : 'warning'
                      }
                      variant="outlined"
                    />
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Contact Statistics */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AssessmentIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Contact Statistics</Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary">
                      {claims.length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Related Claims
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="success.main">
                      {claims.filter(claim => claim.status === 'ACTIVE').length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active Claims
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="warning.main">
                      {claims.filter(claim => claim.status === 'PENDING').length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Pending Claims
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="info.main">
                      {dayjs(representative.createdAt).format('MMM YYYY')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Member Since
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Related Claims */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Related Claims</Typography>
              </Box>
              
              {claimsLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : claims.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Claim Number</TableCell>
                        <TableCell>File Number</TableCell>
                        <TableCell>Insured Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Date Reported</TableCell>
                        <TableCell>Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {claims.map((claim) => (
                        <TableRow key={claim.id} hover>
                          <TableCell>
                            <Button
                              variant="text"
                              onClick={() => navigate(`/claims/${claim.file_number}`)}
                              sx={{ p: 0, textTransform: 'none' }}
                            >
                              {claim.claimNumber}
                            </Button>
                          </TableCell>
                          <TableCell>{claim.file_number}</TableCell>
                          <TableCell>{claim.insuredName || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip
                              label={claim.status}
                              color={
                                claim.status === 'ACTIVE' ? 'success' :
                                claim.status === 'PENDING' ? 'warning' : 'error'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {claim.dateReported ? dayjs(claim.dateReported).format('MM/DD/YYYY') : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {claim.amount ? `$${claim.amount.toLocaleString()}` : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box textAlign="center" p={3}>
                  <Typography color="textSecondary">
                    No related claims found for this representative.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InsuranceRepresentativeDetails;
